<template>
  <div
    class="latex-content"
    :class="{ 'latex-content--rtl': isRtl }"
    v-html="renderedContent"
  ></div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";

export default {
  name: "LatexRenderer",
  props: {
    content: {
      type: String,
      default: "",
    },
    isRtl: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const katexLoaded = ref(false);

    // Load KaTeX dynamically
    const loadKatex = async () => {
      try {
        if (window.katex) {
          katexLoaded.value = true;
          return;
        }

        // Load KaTeX CSS
        if (!document.querySelector('link[href*="katex"]')) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href =
            "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css";
          link.crossOrigin = "anonymous";
          document.head.appendChild(link);
        }

        // Load KaTeX JS
        if (!document.querySelector('script[src*="katex.min.js"]')) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js";
            script.crossOrigin = "anonymous";
            script.onload = resolve;
            script.onerror = () => {
              console.warn("[LatexRenderer] Failed to load KaTeX");
              resolve(); // Don't reject, just continue without KaTeX
            };
            document.head.appendChild(script);
          });
        }

        katexLoaded.value = !!window.katex;
      } catch (e) {
        console.warn("[LatexRenderer] Error loading KaTeX:", e);
        katexLoaded.value = false;
      }
    };

    onMounted(() => {
      loadKatex();
    });

    /**
     * Render LaTeX in content that may already contain HTML
     * Preserves existing HTML tags and only processes LaTeX math
     */
    const renderedContent = computed(() => {
      if (!props.content) return "";

      let html = props.content;

      // Check if content already contains HTML tags
      const hasHtml = /<[a-z][\s\S]*>/i.test(html);

      if (!hasHtml) {
        // Plain text - preserve line breaks
        html = html.replace(/\n/g, "<br>");
      }

      if (!katexLoaded.value || !window.katex) {
        return html;
      }

      try {
        // Process display math: \[ ... \]
        html = html.replace(/\\\[([\s\S]*?)\\\]/g, (match, latex) => {
          try {
            // Unescape any HTML entities in the LaTeX
            const cleanLatex = latex
              .replace(/&lt;/g, "<")
              .replace(/&gt;/g, ">")
              .replace(/&amp;/g, "&")
              .replace(/\\\\xrightarrow/g, "\\xrightarrow")
              .replace(/\\\\Delta/g, "\\Delta")
              .replace(/\\\\uparrow/g, "\\uparrow")
              .replace(/\\\\downarrow/g, "\\downarrow")
              .replace(/\\\\rightarrow/g, "\\rightarrow")
              .replace(/\\\\/g, "\\");
            return `<div class="latex-display">${window.katex.renderToString(
              cleanLatex.trim(),
              {
                displayMode: true,
                throwOnError: false,
                trust: true,
              }
            )}</div>`;
          } catch (e) {
            console.warn("[LatexRenderer] Display math error:", e, latex);
            return `<div class="latex-display latex-error">${latex}</div>`;
          }
        });

        // Process display math: $$ ... $$
        html = html.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
          try {
            const cleanLatex = latex.replace(/\\\\/g, "\\");
            return `<div class="latex-display">${window.katex.renderToString(
              cleanLatex.trim(),
              {
                displayMode: true,
                throwOnError: false,
                trust: true,
              }
            )}</div>`;
          } catch (e) {
            console.warn("[LatexRenderer] Display math error:", e);
            return `<div class="latex-display latex-error">${latex}</div>`;
          }
        });

        // Process inline math: \( ... \)
        html = html.replace(/\\\(([\s\S]*?)\\\)/g, (match, latex) => {
          try {
            const cleanLatex = latex.replace(/\\\\/g, "\\");
            return `<span class="latex-inline">${window.katex.renderToString(
              cleanLatex.trim(),
              {
                displayMode: false,
                throwOnError: false,
                trust: true,
              }
            )}</span>`;
          } catch (e) {
            console.warn("[LatexRenderer] Inline math error:", e);
            return `<span class="latex-inline latex-error">${latex}</span>`;
          }
        });
      } catch (e) {
        console.error("[LatexRenderer] Rendering error:", e);
      }

      return html;
    });

    // Re-render when katex loads
    watch(katexLoaded, () => {
      // Force re-computation
    });

    return {
      renderedContent,
    };
  },
};
</script>

<style>
.latex-content {
  font-size: 16px;
  line-height: 1.8;
  color: #1f2937;
  word-wrap: break-word;
}

.latex-content--rtl {
  direction: rtl;
  text-align: right;
}

.latex-display {
  display: block;
  text-align: center;
  margin: 16px 0;
  padding: 12px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
}

.latex-inline {
  display: inline;
  padding: 0 4px;
}

.latex-error {
  color: #ef4444;
  font-family: monospace;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

/* KaTeX overrides for better styling */
.latex-content .katex {
  font-size: 1.1em;
}

.latex-content .katex-display {
  margin: 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.latex-content .katex-display > .katex {
  white-space: normal;
}

/* Numbered lists and formatting */
.latex-content ol {
  counter-reset: item;
  padding-left: 0;
  list-style: none;
}

.latex-content ol > li {
  counter-increment: item;
  margin-bottom: 16px;
  padding-left: 32px;
  position: relative;
}

.latex-content ol > li::before {
  content: counter(item) ".";
  position: absolute;
  left: 0;
  font-weight: 600;
  color: #6366f1;
}

.latex-content ul {
  padding-left: 24px;
}

.latex-content ul > li {
  margin-bottom: 8px;
  position: relative;
}

.latex-content ul > li::marker {
  color: #6366f1;
}

/* Code blocks */
.latex-content code {
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Fira Code", "Monaco", monospace;
  font-size: 0.9em;
  color: #7c3aed;
}

.latex-content pre {
  background: #1e293b;
  color: #e2e8f0;
  padding: 16px;
  border-radius: 12px;
  overflow-x: auto;
  margin: 16px 0;
}

.latex-content pre code {
  background: transparent;
  color: inherit;
  padding: 0;
}

/* Headings */
.latex-content h1,
.latex-content h2,
.latex-content h3,
.latex-content h4 {
  margin-top: 24px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #111827;
}

.latex-content h1 {
  font-size: 1.5em;
}
.latex-content h2 {
  font-size: 1.3em;
}
.latex-content h3 {
  font-size: 1.15em;
}
.latex-content h4 {
  font-size: 1.05em;
}

/* Paragraphs */
.latex-content p {
  margin-bottom: 12px;
}

/* Strong and emphasis */
.latex-content strong {
  font-weight: 600;
  color: #111827;
}

.latex-content em {
  font-style: italic;
}

/* Blockquotes */
.latex-content blockquote {
  border-left: 4px solid #6366f1;
  padding-left: 16px;
  margin: 16px 0;
  color: #4b5563;
  font-style: italic;
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 0 8px 8px 0;
}

/* Tables */
.latex-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.latex-content th,
.latex-content td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.latex-content th {
  background: #f8fafc;
  font-weight: 600;
  color: #374151;
}

.latex-content tr:last-child td {
  border-bottom: none;
}

.latex-content tr:hover {
  background: #f9fafb;
}
</style>
